// FundsChain 2.0 Spending Plan - Mock Data

//
// Integrated with FundsChain 1.0:
// - Uses existing Categories, Components from project settings
// - Links to existing Contracts and Suppliers
// - Connects to Designated Accounts for fund allocation
//
// Flow: Account Balance → Spending Plan → Work Plans → Activities → Contracts → Conditions → Payment

import {
	AccountOption,
	ActivityOption,
	BulkUploadActivityPreview,
	BulkUploadConditionPreview,
	WorkPlanOption,
	Voucher,
} from './types';

import { ExtendedVoucher, PaymentLocation, VoucherStats } from './types';

// V1 Integration Types (imported from @types in real implementation)

// Reference types for V1 entities - these map to real V1 types
export interface AccountReference {
	id: number;
	name: string;
	num: string;
	currency: string;
	type: 'DESIGNATED' | 'LOCAL';
	institution: string;
}

export interface AccountBalanceReference {
	balance: number;
	currency: string;
	loan: LoanReference | number;
}

export interface LoanReference {
	id: number;
	code: string;
	name: string | null;
	financier: string;
	currency: string;
	amount: number;
	status: 'ACTIVE' | 'DISABLED';
}

export interface CategoryReference {
	id: number;
	name: string;
	allocations?: { amount: number; currency: string; loan: number }[];
}

export interface ComponentReference {
	id: number;
	name: string;
	parent?: number;
	allocations?: { amount: number; currency: string; loan: number }[];
}

export interface EntityReference {
	id: number;
	name: string;
	type: string;
}

export interface ContractReference {
	id: number;
	num: string;
	amount: number;
	currency: string;
	supplier: EntityReference;
	status: string;
}

export interface ExpenseTypeReference {
	id: number;
	name: string;
	status: 'ACTIVE' | 'DEACTIVATED';
}

// Work Plans Types

export type WorkPlanStatus = 'DRAFT' | 'ACTIVE' | 'LOCKED' | 'COMPLETED';

export interface WorkPlanPayload {
	name: string;
	description: string;
	// V1 Integration - Source of funds
	projectId?: number;
	accountId: number;
	accountName: string;
	loanId: number;
	loanCode: string;
	entityId?: number; // Sub-PIU that manages this work plan
	// V1 Classification (optional - tag with any combination)
	categoryId?: number;
	categoryName?: string;
	componentId?: number;
	componentName?: string;
	expenseTypeId?: number; // V1 Expense Type (Goods, Works, Services, etc.)
	expenseTypeName?: string;
	// Parent work plan (for sub-work plans)
	parentWorkPlanId?: number;
	// Time period
	fiscalYear?: string;
	dateStart?: string;
	dateEnd?: string;
	// Amount to allocate
	amount: number;
	/** @deprecated Use amount - kept for UI compatibility */
	plannedAmount: number;
	currency: string;
	// Optional conditions
	conditions?: WorkPlanConditionPayload[];
}

export interface WorkPlanConditionPayload {
	name: string;
	description: string;
	isRequired: boolean;
}

export interface WorkPlan {
	id: number;
	name: string;
	description: string;
	// V1 Integration - Source of funds
	projectId?: number; // Links to V1 Project
	accountId: number;
	accountName: string;
	loanId: number;
	loanCode: string;
	entityId?: number; // Sub-PIU that manages this work plan
	entityName?: string;
	// V1 Classification (optional - tag with any combination)
	categoryId?: number;
	categoryName?: string;
	componentId?: number;
	componentName?: string;
	expenseTypeId?: number; // V1 Expense Type (Goods, Works, Services, etc.)
	expenseTypeName?: string;
	// Parent work plan (for sub-work plans by category)
	parentWorkPlanId?: number;
	// Sub-work plans (categories under this component)
	subWorkPlans?: WorkPlan[];
	// Time period
	fiscalYear?: string;
	dateStart?: string;
	dateEnd?: string;
	// Amount - single source of truth (committed/spent derived from children)
	amount: number;
	/** @deprecated Use amount instead - kept for UI compatibility */
	plannedAmount: number;
	/** @deprecated Derived from child activities/contracts - kept for UI compatibility */
	committedAmount: number;
	/** @deprecated Derived from redeemed vouchers - kept for UI compatibility */
	spentAmount: number;
	/** @deprecated Derived: amount - committedAmount - kept for UI compatibility */
	availableAmount: number;
	currency: string;
	// Settings
	status: WorkPlanStatus;
	// Conditions attached to this work plan
	conditions: WorkPlanConditions[];
	// Timestamps
	createdAt: string;
	modifiedAt: string;
}

export interface WorkPlanConditions {
	id: number;
	name: string;
	description: string;
	isRequired: boolean;
	isMet: boolean;
	// DB-aligned fields
	enforcementType?: 'ENFORCE' | 'OBSERVE';
	// Verification
	status?: 'PENDING' | 'SUBMITTED' | 'VERIFIED' | 'APPROVED' | 'REJECTED';
	verifiedAt?: string;
	verifiedBy?: string;
	// Document
	documentId?: number;
}

// Activity Types (Work items within an Work Plan - can be at component or category level)

export type ActivityStatus = 'PLANNED' | 'IN_PROGRESS' | 'COMPLETED' | 'DELAYED' | 'CANCELLED';

export interface Activity {
	id: number;
	workPlanId: number;
	parentActivityId?: number; // Parent activity ID for sub-activities
	// V1 Integration
	entityId: number; // Responsible Sub-PIU
	/** @deprecated Use entityId - kept for UI compatibility */
	responsibleEntityId: number;
	/** @deprecated Denormalized - kept for UI compatibility */
	responsibleEntity: string; // Entity name from V1
	// Details
	name: string;
	description: string;
	// Amount - single source of truth
	amount: number;
	/** @deprecated Use amount - kept for UI compatibility */
	plannedAmount: number;
	/** @deprecated Derived from child contracts - kept for UI compatibility */
	committedAmount: number;
	/** @deprecated Derived from redeemed vouchers - kept for UI compatibility */
	spentAmount: number;
	currency: string;
	amountLocal?: number;
	currencyLocal?: string;
	// Schedule
	dateStart?: string;
	dateEnd?: string;
	/** @deprecated Use dateStart - kept for UI compatibility */
	startDate: string;
	/** @deprecated Use dateEnd - kept for UI compatibility */
	endDate: string;
	progress: number; // 0-100
	status: ActivityStatus;
	// Linked contracts from V1
	contracts: ActivityContract[];
	// Sub-activities
	subActivities?: Activity[];
}

export interface ActivityContract {
	id: number; // Contract ID
	v1ContractId?: number; // V1 Contract ID if linked
	contractNum: string;
	/** @deprecated Denormalized - kept for UI compatibility */
	supplierName: string;
	supplierId: number;
	amount: number;
	currency: string;
	status: 'ACTIVE' | 'COMPLETED' | 'CANCELLED';
	// Conditions for payment release
	conditions: ContractCondition[];
	// Payment tracking - deprecated, derived from vouchers
	/** @deprecated Derived from vouchers WHERE status='REDEEMED' */
	paidAmount: number;
	/** @deprecated Derived: amount - paidAmount */
	remainingAmount: number;
}

// Condition Exception Types (for Observe Mode overrides)

export interface ConditionException {
	id: number;
	// References (following V1 naming convention - entity name, not entityId)
	activity: number;
	activityName: string;
	condition: number;
	conditionName: string;
	contract: number;
	contractNum: string;
	// Override details
	notificationRecipient?: string;
	notificationRecipientName?: string;
	notificationSent: boolean;
	overriddenAt: string;
	overriddenBy: number;
	overriddenByName: string;
	reason: string;
}

export interface ContractCondition {
	id: number;
	name: string;
	description: string;
	paymentPercentage: number; // % of contract released when met
	paymentAmount: number;
	sequenceOrder: number;
	status: 'PENDING' | 'SUBMITTED' | 'VERIFIED' | 'APPROVED' | 'REJECTED';
	verifiedAt?: string;
	verifiedBy?: string;
	// Supporting document
	// Enforcement type - inherits from work plan if not set
	enforcementType?: 'ENFORCE' | 'OBSERVE';
	// Exception tracking for overridden conditions (Observe mode)
	exception?: ConditionException;
}

// Dashboard Summary Types

export interface DashboardSummary {
	totalBudget: number;
	totalPlanned: number; // Was totalAllocated
	totalCommitted: number;
	totalSpent: number;
	totalAvailable: number;
	currency: string;
	workPlanCount: number;
	activityCount: number;
	contractCount: number;
	pendingConditions: number;
	readyForPayment: number; // Payments ready for redemption
}

// Mock Data - Using Component-based hierarchy with Category sub-work plans

// Work Plans - Top level by Component, with optional Category sub-work plans
export const mockWorkPlans: WorkPlan[] = [
	{
		id: 1,
		projectId: 100,
		name: 'Flood Infrastructure',
		description: 'Primary infrastructure component for flood management',
		accountId: 101,
		accountName: 'DA-A (Designated Account)',
		loanId: 1,
		loanCode: 'IBRD-9001',
		entityId: 101,
		entityName: 'Metro Manila Development Authority',
		componentId: 1,
		componentName: 'Flood Infrastructure',
		expenseTypeId: 1,
		expenseTypeName: 'Works',
		fiscalYear: 'FY2025',
		amount: 2500000,
		plannedAmount: 2500000,
		committedAmount: 2100000,
		spentAmount: 950000,
		availableAmount: 400000,
		currency: 'USD',
		status: 'ACTIVE',
		// sub-work plans by category
		subWorkPlans: [
			{
				id: 11,
				projectId: 100,
				name: 'Civil Works',
				description: 'Construction and civil works under Flood Infrastructure',
				accountId: 101,
				accountName: 'DA-A (Designated Account)',
				loanId: 1,
				loanCode: 'IBRD-9001',
				categoryId: 1,
				categoryName: 'Civil Works',
				expenseTypeId: 1,
				expenseTypeName: 'Works',
				parentWorkPlanId: 1,
				amount: 1800000,
				plannedAmount: 1800000,
				committedAmount: 1500000,
				spentAmount: 700000,
				availableAmount: 300000,
				currency: 'USD',
				status: 'ACTIVE',
				conditions: [],
				createdAt: '2024-12-01',
				modifiedAt: '2025-01-15',
			},
			{
				id: 12,
				projectId: 100,
				name: 'Goods & Equipment',
				description: 'Equipment and materials under Flood Infrastructure',
				accountId: 101,
				accountName: 'DA-A (Designated Account)',
				loanId: 1,
				loanCode: 'IBRD-9001',
				categoryId: 3,
				categoryName: 'Goods',
				expenseTypeId: 2,
				expenseTypeName: 'Goods',
				parentWorkPlanId: 1,
				amount: 700000,
				plannedAmount: 700000,
				committedAmount: 600000,
				spentAmount: 250000,
				availableAmount: 100000,
				currency: 'USD',
				status: 'ACTIVE',
				conditions: [],
				createdAt: '2024-12-01',
				modifiedAt: '2025-01-15',
			},
		],
		conditions: [
			{
				id: 1,
				name: 'Environmental Clearance',
				description: 'Must have environmental impact assessment approved',
				isRequired: true,
				isMet: true,
				enforcementType: 'ENFORCE',
				status: 'APPROVED',
				verifiedAt: '2024-12-15T10:00:00Z',
				verifiedBy: 'Environmental Officer',
			},
			{
				id: 2,
				name: 'Site Inspection',
				description: 'Physical site inspection by PIU',
				isRequired: true,
				isMet: true,
				enforcementType: 'ENFORCE',
				status: 'APPROVED',
				verifiedAt: '2024-12-20T14:30:00Z',
				verifiedBy: 'PIU Inspector',
			},
		],
		createdAt: '2024-12-01',
		modifiedAt: '2025-01-15',
	},
	{
		id: 2,
		projectId: 100,
		name: 'Technical Assistance',
		description: 'Engineering design and project management support',
		accountId: 101,
		accountName: 'DA-A (Designated Account)',
		loanId: 1,
		loanCode: 'IBRD-9001',
		entityId: 101,
		entityName: 'Metro Manila Development Authority',
		componentId: 2,
		componentName: 'Technical Assistance',
		expenseTypeId: 3,
		expenseTypeName: 'Services',
		fiscalYear: 'FY2025',
		amount: 1000000,
		plannedAmount: 1000000,
		committedAmount: 750000,
		spentAmount: 500000,
		availableAmount: 250000,
		currency: 'USD',
		status: 'ACTIVE',
		subWorkPlans: [
			{
				id: 21,
				projectId: 100,
				name: 'Consulting Services',
				description: 'Technical consulting and advisory',
				accountId: 101,
				accountName: 'DA-A (Designated Account)',
				loanId: 1,
				loanCode: 'IBRD-9001',
				categoryId: 2,
				categoryName: 'Consulting Services',
				expenseTypeId: 3,
				expenseTypeName: 'Services',
				parentWorkPlanId: 2,
				amount: 1000000,
				plannedAmount: 1000000,
				committedAmount: 750000,
				spentAmount: 500000,
				availableAmount: 250000,
				currency: 'USD',
				status: 'ACTIVE',
				conditions: [],
				createdAt: '2024-12-01',
				modifiedAt: '2025-01-10',
			},
		],
		conditions: [
			{
				id: 3,
				name: 'Qualification Verified',
				description: 'Consultant qualifications verified',
				isRequired: true,
				isMet: true,
			},
		],
		createdAt: '2024-12-01',
		modifiedAt: '2025-01-10',
	},
	{
		id: 3,
		projectId: 100,
		name: 'Capacity Building',
		description: 'Training and institutional strengthening',
		accountId: 101,
		accountName: 'DA-A (Designated Account)',
		loanId: 1,
		loanCode: 'IBRD-9001',
		entityId: 101,
		entityName: 'Metro Manila Development Authority',
		componentId: 3,
		componentName: 'Capacity Building',
		expenseTypeId: 3,
		expenseTypeName: 'Services',
		fiscalYear: 'FY2025',
		amount: 500000,
		plannedAmount: 500000,
		committedAmount: 350000,
		spentAmount: 300000,
		availableAmount: 150000,
		currency: 'USD',
		status: 'ACTIVE',
		conditions: [
			{
				id: 4,
				name: 'Training Plan Approved',
				description: 'Annual training plan approved by PIU',
				isRequired: true,
				isMet: false,
			},
		],
		createdAt: '2024-12-05',
		modifiedAt: '2025-01-12',
	},
	{
		id: 4,
		projectId: 100,
		name: 'Project Management',
		description: 'Project management and operational costs',
		accountId: 101,
		accountName: 'DA-A (Designated Account)',
		loanId: 1,
		loanCode: 'IBRD-9001',
		entityId: 101,
		entityName: 'Metro Manila Development Authority',
		componentId: 4,
		componentName: 'Project Management',
		expenseTypeId: 4,
		expenseTypeName: 'Operating Costs',
		fiscalYear: 'FY2025',
		amount: 200000,
		plannedAmount: 200000,
		committedAmount: 150000,
		spentAmount: 100000,
		availableAmount: 50000,
		currency: 'USD',
		status: 'ACTIVE',
		subWorkPlans: [
			{
				id: 41,
				projectId: 100,
				name: 'Operating Costs',
				description: 'Day-to-day operational expenses',
				accountId: 101,
				accountName: 'DA-A (Designated Account)',
				loanId: 1,
				loanCode: 'IBRD-9001',
				categoryId: 4,
				categoryName: 'Operating Costs',
				expenseTypeId: 4,
				expenseTypeName: 'Operating Costs',
				parentWorkPlanId: 4,
				amount: 200000,
				plannedAmount: 200000,
				committedAmount: 150000,
				spentAmount: 100000,
				availableAmount: 50000,
				currency: 'USD',
				status: 'ACTIVE',
				conditions: [],
				createdAt: '2024-12-10',
				modifiedAt: '2025-01-08',
			},
		],
		conditions: [],
		createdAt: '2024-12-10',
		modifiedAt: '2025-01-08',
	},
];

export const mockActivities: Activity[] = [
	{
		id: 1,
		workPlanId: 1,
		name: 'Drainage Channel Construction - Phase 1',
		description: 'Construction of primary drainage channels in District 1',
		entityId: 101,
		responsibleEntity: 'Metro Manila Development Authority',
		responsibleEntityId: 101,
		amount: 1200000,
		plannedAmount: 1200000,
		committedAmount: 1100000,
		spentAmount: 550000,
		currency: 'USD',
		dateStart: '2025-01-15',
		dateEnd: '2025-06-30',
		startDate: '2025-01-15',
		endDate: '2025-06-30',
		progress: 45,
		status: 'IN_PROGRESS',
		contracts: [
			{
				id: 1001,
				contractNum: 'CW-2025-001',
				supplierName: 'Alstom Construction',
				supplierId: 201,
				amount: 800000,
				currency: 'USD',
				status: 'ACTIVE',
				paidAmount: 400000,
				remainingAmount: 400000,
				conditions: [
					{
						id: 101,
						name: 'Mobilization Complete',
						description: 'Site mobilization and setup',
						paymentPercentage: 20,
						paymentAmount: 160000,
						sequenceOrder: 1,
						status: 'APPROVED',
						verifiedAt: '2025-02-01',
						verifiedBy: 'Engineer A',
					},
					{
						id: 102,
						name: '30% Progress',
						description: '30% of work completed',
						paymentPercentage: 30,
						paymentAmount: 240000,
						sequenceOrder: 2,
						status: 'APPROVED',
						verifiedAt: '2025-03-15',
						verifiedBy: 'Engineer A',
					},
					{
						id: 103,
						name: '60% Progress',
						description: '60% of work completed',
						paymentPercentage: 25,
						paymentAmount: 200000,
						sequenceOrder: 3,
						status: 'SUBMITTED',
					},
					{
						id: 104,
						name: 'Final Completion',
						description: 'Work 100% complete',
						paymentPercentage: 25,
						paymentAmount: 200000,
						sequenceOrder: 4,
						status: 'PENDING',
					},
				],
			},
			{
				id: 1002,
				contractNum: 'CW-2025-002',
				supplierName: 'PhilConstruct Inc.',
				supplierId: 202,
				amount: 300000,
				currency: 'USD',
				status: 'ACTIVE',
				paidAmount: 150000,
				remainingAmount: 150000,
				conditions: [
					{
						id: 105,
						name: 'Initial Payment',
						description: 'Upon contract signing',
						paymentPercentage: 50,
						paymentAmount: 150000,
						sequenceOrder: 1,
						status: 'APPROVED',
						verifiedAt: '2025-01-20',
						verifiedBy: 'Finance Officer',
						enforcementType: 'ENFORCE',
					},
					{
						id: 106,
						name: 'Final Payment',
						description: 'Upon delivery completion',
						paymentPercentage: 50,
						paymentAmount: 150000,
						sequenceOrder: 2,
						status: 'PENDING',
						enforcementType: 'OBSERVE',
					},
				],
			},
		],
		// Sub-activities for hierarchical structure
		subActivities: [
			{
				id: 11,
				workPlanId: 1,
				parentActivityId: 1,
				name: 'Site Preparation & Excavation',
				description: 'Clear site and excavate primary channel routes',
				responsibleEntity: 'Metro Manila Development Authority',
				responsibleEntityId: 101,
				entityId: 101,
				plannedAmount: 400000,
				amount: 400000,
				committedAmount: 380000,
				spentAmount: 200000,
				currency: 'USD',
				startDate: '2025-01-15',
				endDate: '2025-03-15',
				dateStart: '2025-01-15',
				dateEnd: '2025-03-15',
				progress: 75,
				status: 'IN_PROGRESS',
				contracts: [],
			},
			{
				id: 12,
				workPlanId: 1,
				parentActivityId: 1,
				name: 'Channel Lining & Concrete Works',
				description: 'Install concrete lining for drainage channels',
				responsibleEntity: 'Metro Manila Development Authority',
				responsibleEntityId: 101,
				entityId: 101,
				plannedAmount: 500000,
				amount: 500000,
				committedAmount: 450000,
				spentAmount: 250000,
				currency: 'USD',
				startDate: '2025-03-01',
				endDate: '2025-05-30',
				dateStart: '2025-03-01',
				dateEnd: '2025-05-30',
				progress: 40,
				status: 'IN_PROGRESS',
				contracts: [],
			},
			{
				id: 13,
				workPlanId: 1,
				parentActivityId: 1,
				name: 'Outlet Structures & Gates',
				description: 'Install outlet control structures and flood gates',
				responsibleEntity: 'Metro Manila Development Authority',
				responsibleEntityId: 101,
				entityId: 101,
				plannedAmount: 300000,
				amount: 300000,
				committedAmount: 270000,
				spentAmount: 100000,
				currency: 'USD',
				startDate: '2025-05-01',
				endDate: '2025-06-30',
				dateStart: '2025-05-01',
				dateEnd: '2025-06-30',
				progress: 20,
				status: 'PLANNED',
				contracts: [],
			},
		],
	},
	{
		id: 2,
		workPlanId: 1,
		name: 'Pump Station Rehabilitation',
		description: 'Rehabilitation of existing pump stations',
		responsibleEntity: 'Metro Manila Development Authority',
		responsibleEntityId: 101,
		entityId: 101,
		plannedAmount: 800000,
		amount: 800000,
		committedAmount: 700000,
		spentAmount: 400000,
		currency: 'USD',
		startDate: '2025-02-01',
		endDate: '2025-08-31',
		dateStart: '2025-02-01',
		dateEnd: '2025-08-31',
		progress: 55,
		status: 'IN_PROGRESS',
		contracts: [
			{
				id: 1003,
				contractNum: 'CW-2025-003',
				supplierName: 'HydroTech Solutions',
				supplierId: 203,
				amount: 700000,
				currency: 'USD',
				status: 'ACTIVE',
				paidAmount: 400000,
				remainingAmount: 300000,
				conditions: [
					{
						id: 107,
						name: 'Equipment Delivery',
						description: 'Pump equipment delivered',
						paymentPercentage: 40,
						paymentAmount: 280000,
						sequenceOrder: 1,
						status: 'APPROVED',
						verifiedAt: '2025-03-01',
						verifiedBy: 'Procurement',
						enforcementType: 'ENFORCE',
					},
					{
						id: 108,
						name: 'Installation Complete',
						description: 'Pumps installed and tested',
						paymentPercentage: 40,
						paymentAmount: 280000,
						sequenceOrder: 2,
						status: 'VERIFIED',
						enforcementType: 'ENFORCE',
					},
					{
						id: 109,
						name: 'Commissioning',
						description: 'Final commissioning and handover',
						paymentPercentage: 20,
						paymentAmount: 140000,
						sequenceOrder: 3,
						status: 'PENDING',
						enforcementType: 'OBSERVE',
					},
				],
			},
		],
		// Sub-activities for pump station work
		subActivities: [
			{
				id: 21,
				workPlanId: 1,
				parentActivityId: 2,
				name: 'Pump Station A - Marikina',
				description: 'Rehabilitation of Marikina River pump station',
				responsibleEntity: 'Metro Manila Development Authority',
				responsibleEntityId: 101,
				entityId: 101,
				plannedAmount: 350000,
				amount: 350000,
				committedAmount: 320000,
				spentAmount: 200000,
				currency: 'USD',
				startDate: '2025-02-01',
				endDate: '2025-05-31',
				dateStart: '2025-02-01',
				dateEnd: '2025-05-31',
				progress: 70,
				status: 'IN_PROGRESS',
				contracts: [],
			},
			{
				id: 22,
				workPlanId: 1,
				parentActivityId: 2,
				name: 'Pump Station B - Pasig',
				description: 'Rehabilitation of Pasig River pump station',
				responsibleEntity: 'Metro Manila Development Authority',
				responsibleEntityId: 101,
				entityId: 101,
				plannedAmount: 300000,
				amount: 300000,
				committedAmount: 280000,
				spentAmount: 150000,
				currency: 'USD',
				startDate: '2025-03-01',
				endDate: '2025-07-31',
				dateStart: '2025-03-01',
				dateEnd: '2025-07-31',
				progress: 45,
				status: 'IN_PROGRESS',
				contracts: [],
			},
			{
				id: 23,
				workPlanId: 1,
				parentActivityId: 2,
				name: 'Pump Station C - Taguig',
				description: 'Rehabilitation of Taguig lagoon pump station',
				responsibleEntity: 'Metro Manila Development Authority',
				responsibleEntityId: 101,
				entityId: 101,
				plannedAmount: 150000,
				amount: 150000,
				committedAmount: 100000,
				spentAmount: 50000,
				currency: 'USD',
				startDate: '2025-05-01',
				endDate: '2025-08-31',
				dateStart: '2025-05-01',
				dateEnd: '2025-08-31',
				progress: 25,
				status: 'PLANNED',
				contracts: [],
			},
		],
	},
	{
		id: 3,
		workPlanId: 2,
		name: 'Technical Advisory Services',
		description: 'Engineering design and supervision consultancy',
		responsibleEntity: 'Project Implementation Unit',
		responsibleEntityId: 100,
		entityId: 100,
		plannedAmount: 500000,
		amount: 500000,
		committedAmount: 500000,
		spentAmount: 350000,
		currency: 'USD',
		startDate: '2025-01-01',
		endDate: '2025-12-31',
		dateStart: '2025-01-01',
		dateEnd: '2025-12-31',
		progress: 70,
		status: 'IN_PROGRESS',
		contracts: [
			{
				id: 1004,
				contractNum: 'CS-2025-001',
				supplierName: 'AECOM Philippines',
				supplierId: 204,
				amount: 500000,
				currency: 'USD',
				status: 'ACTIVE',
				paidAmount: 350000,
				remainingAmount: 150000,
				conditions: [
					{
						id: 110,
						name: 'Inception Report',
						description: 'Inception report approved',
						paymentPercentage: 20,
						paymentAmount: 100000,
						sequenceOrder: 1,
						status: 'APPROVED',
						verifiedAt: '2025-01-30',
						verifiedBy: 'Project Manager',
					},
					{
						id: 111,
						name: 'Quarterly Report Q1',
						description: 'Q1 progress report',
						paymentPercentage: 25,
						paymentAmount: 125000,
						sequenceOrder: 2,
						status: 'APPROVED',
						verifiedAt: '2025-04-15',
						verifiedBy: 'Project Manager',
					},
					{
						id: 112,
						name: 'Quarterly Report Q2',
						description: 'Q2 progress report',
						paymentPercentage: 25,
						paymentAmount: 125000,
						sequenceOrder: 3,
						status: 'APPROVED',
						verifiedAt: '2025-07-15',
						verifiedBy: 'Project Manager',
					},
					{
						id: 113,
						name: 'Final Report',
						description: 'Final deliverables',
						paymentPercentage: 30,
						paymentAmount: 150000,
						sequenceOrder: 4,
						status: 'PENDING',
					},
				],
			},
		],
	},
	{
		id: 4,
		workPlanId: 3,
		name: 'Heavy Equipment Procurement',
		description: 'Procurement of excavators and dump trucks',
		responsibleEntity: 'Metro Manila Development Authority',
		responsibleEntityId: 101,
		entityId: 101,
		plannedAmount: 350000,
		amount: 350000,
		committedAmount: 350000,
		spentAmount: 300000,
		currency: 'USD',
		startDate: '2025-01-01',
		endDate: '2025-03-31',
		dateStart: '2025-01-01',
		dateEnd: '2025-03-31',
		progress: 85,
		status: 'IN_PROGRESS',
		contracts: [
			{
				id: 1005,
				contractNum: 'GD-2025-001',
				supplierName: 'CAT Equipment Dealer',
				supplierId: 205,
				amount: 350000,
				currency: 'USD',
				status: 'ACTIVE',
				paidAmount: 300000,
				remainingAmount: 50000,
				conditions: [
					{
						id: 114,
						name: 'Advance Payment',
						description: '30% advance upon PO',
						paymentPercentage: 30,
						paymentAmount: 105000,
						sequenceOrder: 1,
						status: 'APPROVED',
						verifiedAt: '2025-01-15',
						verifiedBy: 'Finance',
					},
					{
						id: 115,
						name: 'Delivery Payment',
						description: '60% upon delivery',
						paymentPercentage: 60,
						paymentAmount: 210000,
						sequenceOrder: 2,
						status: 'APPROVED',
						verifiedAt: '2025-02-28',
						verifiedBy: 'Warehouse',
					},
					{
						id: 116,
						name: 'Retention Release',
						description: '10% after warranty period',
						paymentPercentage: 10,
						paymentAmount: 35000,
						sequenceOrder: 3,
						status: 'PENDING',
						enforcementType: 'OBSERVE',
					},
				],
			},
		],
	},
];

// Mock Exceptions Data (Conditions that were overridden in Observe mode)

export const mockExceptions: ConditionException[] = [
	{
		id: 1,
		activity: 1,
		activityName: 'Drainage Channel Construction - Phase 1',
		condition: 106,
		conditionName: 'Final Payment',
		contract: 1002,
		contractNum: 'CW-2025-002',
		notificationRecipient: 'piu.manager@worldbank.org',
		notificationRecipientName: 'Juan dela Cruz',
		notificationSent: true,
		overriddenAt: '2025-03-10T14:30:00Z',
		overriddenBy: 101,
		overriddenByName: 'Maria Santos',
		reason:
			'Urgent payment needed due to supplier cash flow issues. Documents will be submitted within 5 business days.',
	},
	{
		id: 2,
		activity: 2,
		activityName: 'Pump Station Rehabilitation',
		condition: 109,
		conditionName: 'Commissioning',
		contract: 1003,
		contractNum: 'CW-2025-003',
		notificationRecipient: 'piu.manager@worldbank.org',
		notificationRecipientName: 'Juan dela Cruz',
		notificationSent: true,
		overriddenAt: '2025-03-12T09:15:00Z',
		overriddenBy: 102,
		overriddenByName: 'Carlos Reyes',
		reason:
			'Partial commissioning complete. Final testing delayed due to equipment availability. Payment approved by TTL.',
	},
];

// Mock Vouchers Data (Digital vouchers for payment tracking)

// Vouchers are generated when conditions are met and track the full payment lifecycle.
// Status flow: GENERATED → VERIFIED → READY → REDEEMED (or CANCELLED)

export const mockVouchers: Voucher[] = [
	// Vendor voucher - Mobilization payment (REDEEMED)
	{
		id: 1,
		activityId: 1,
		conditionId: 101,
		contractId: 1001,
		recipientEntityId: 201,
		recipientType: 'SUPPLIER',
		amount: 160000,
		currency: 'USD',
		status: 'REDEEMED',
		channel: 'BANK',
		bankName: 'BDO Unibank',
		bankAccountNum: '****4567',
		bankRoutingNum: '010530667',
		redeemedAt: '2025-01-25T10:30:00Z',
		dateProcessed: '2025-01-26',
		referenceNum: 'PAY-2025-001',
		created: '2025-01-20T09:00:00Z',
		modified: '2025-01-26T14:00:00Z',
	},
	// Vendor voucher - Progress payment (REDEEMED)
	{
		id: 2,
		activityId: 1,
		conditionId: 102,
		contractId: 1001,
		recipientEntityId: 201,
		recipientType: 'SUPPLIER',
		amount: 270000,
		currency: 'USD',
		status: 'REDEEMED',
		channel: 'BANK',
		bankName: 'BDO Unibank',
		bankAccountNum: '****4567',
		redeemedAt: '2025-03-12T11:15:00Z',
		dateProcessed: '2025-03-13',
		referenceNum: 'PAY-2025-012',
		created: '2025-03-10T08:00:00Z',
		modified: '2025-03-13T16:00:00Z',
	},
	// Vendor voucher - Pending verification (GENERATED)
	{
		id: 3,
		activityId: 1,
		conditionId: 103,
		contractId: 1001,
		recipientEntityId: 201,
		recipientType: 'SUPPLIER',
		amount: 200000,
		currency: 'USD',
		status: 'GENERATED',
		created: '2025-06-01T09:00:00Z',
	},
	// Vendor voucher - Ready for payment (READY)
	{
		id: 4,
		activityId: 2,
		conditionId: 107,
		contractId: 1003,
		recipientEntityId: 203,
		recipientType: 'SUPPLIER',
		amount: 280000,
		currency: 'USD',
		status: 'READY',
		channel: 'BANK',
		bankName: 'Metrobank',
		bankAccountNum: '****8901',
		created: '2025-03-02T10:00:00Z',
		modified: '2025-03-05T14:30:00Z',
	},
	// Beneficiary voucher - Cash transfer (REDEEMED via mobile money)
	{
		id: 5,
		activityId: 10,
		recipientEntityId: 1001,
		recipientType: 'BENEFICIARY',
		amount: 30,
		currency: 'USD',
		status: 'REDEEMED',
		channel: 'MOBILE_MONEY',
		mobileProvider: 'GCash',
		mobileNumber: '****5678',
		redeemedAt: '2025-02-15T08:30:00Z',
		dateProcessed: '2025-02-15',
		referenceNum: 'BEN-2025-0001',
		created: '2025-02-14T09:00:00Z',
		modified: '2025-02-15T08:30:00Z',
	},
	// Vendor voucher - Failed payment (needs retry)
	{
		id: 6,
		activityId: 3,
		conditionId: 110,
		contractId: 1004,
		recipientEntityId: 204,
		recipientType: 'SUPPLIER',
		amount: 100000,
		currency: 'USD',
		status: 'READY',
		channel: 'BANK',
		bankName: 'Bank of the Philippine Islands',
		bankAccountNum: '****2345',
		failureReason: 'Invalid account number - please verify bank details',
		created: '2025-01-28T11:00:00Z',
		modified: '2025-02-01T09:15:00Z',
	},
	// Cancelled voucher
	{
		id: 7,
		activityId: 4,
		conditionId: 116,
		contractId: 1005,
		recipientEntityId: 205,
		recipientType: 'SUPPLIER',
		amount: 35000,
		currency: 'USD',
		status: 'CANCELLED',
		created: '2025-03-01T10:00:00Z',
		modified: '2025-03-15T16:00:00Z',
	},
];

export const mockDashboardSummary: DashboardSummary = {
	totalBudget: 5000000,
	totalPlanned: 4200000,
	totalCommitted: 3350000,
	totalSpent: 1850000,
	totalAvailable: 850000,
	currency: 'USD',
	workPlanCount: 4,
	activityCount: 4,
	contractCount: 5,
	pendingConditions: 6,
	readyForPayment: 2,
};

// Helper to get activities by work plan
export const getActivitiesByWorkPlan = (workPlanId: number): Activity[] => {
	return mockActivities.filter((a) => a.workPlanId === workPlanId);
};

// Helper to get work plan by ID
export const getWorkPlanById = (workPlanId: number): WorkPlan | undefined => {
	return mockWorkPlans.find((e) => e.id === workPlanId);
};

// Helper to get all exceptions
export const getExceptions = (): ConditionException[] => {
	return mockExceptions;
};

// Helper to get exception by condition ID
export const getExceptionByCondition = (condition: number): ConditionException | undefined => {
	return mockExceptions.find((e) => e.condition === condition);
};

// Helper to get all conditions that are ready for payment
export const getConditionsReadyForPayment = (): {
	activity: Activity;
	contract: ActivityContract;
	condition: ContractCondition;
}[] => {
	const ready: { activity: Activity; contract: ActivityContract; condition: ContractCondition }[] =
		[];
	mockActivities.forEach((activity) => {
		activity.contracts.forEach((contract) => {
			contract.conditions.forEach((condition) => {
				if (condition.status === 'VERIFIED' || condition.status === 'APPROVED') {
					// Find conditions where previous ones are approved
					const prevConditions = contract.conditions.filter(
						(c) => c.sequenceOrder < condition.sequenceOrder
					);
					const allPrevApproved = prevConditions.every((c) => c.status === 'APPROVED');
					if (allPrevApproved && condition.status === 'VERIFIED') {
						ready.push({ activity, contract, condition });
					}
				}
			});
		});
	});
	return ready;
};

// Bulk Upload Preview Mock Data

export const mockBulkUploadActivitiesPreview: BulkUploadActivityPreview[] = [
	{
		budget: 450000,
		endDate: '2025-06-30',
		workPlan: 'Flood Infrastructure',
		name: 'Water Treatment Facility',
		startDate: '2025-01-15',
	},
	{
		budget: 320000,
		endDate: '2025-08-15',
		workPlan: 'Transportation',
		name: 'Road Rehabilitation Phase 2',
		startDate: '2025-02-01',
	},
	{
		budget: 180000,
		endDate: '2025-09-30',
		workPlan: 'Education Infrastructure',
		name: 'School Building Renovation',
		startDate: '2025-03-01',
	},
];

export const mockBulkUploadConditionsPreview: BulkUploadConditionPreview[] = [
	{
		appliesTo: 'Flood Infrastructure',
		enforcement: 'ENFORCE',
		name: 'Invoice Documentation Required',
		type: 'Document',
	},
	{
		appliesTo: 'Water Treatment Facility',
		enforcement: 'ENFORCE',
		name: 'Site Inspection Complete',
		type: 'Verification',
	},
	{
		appliesTo: 'All Activities',
		enforcement: 'OBSERVE',
		name: 'Environmental Compliance Certificate',
		type: 'Document',
	},
];

// Wizard Mock Data (PageWizard)

export const mockWizardAccounts: AccountOption[] = [
	{
		balance: 2500000,
		currency: 'USD',
		id: 101,
		loanCode: 'IBRD-9001',
		loanId: 1,
		name: 'DA-A (Designated Account)',
		num: 'DA-001',
	},
	{
		balance: 1200000,
		currency: 'USD',
		id: 102,
		loanCode: 'IBRD-9001',
		loanId: 1,
		name: 'DA-B (Secondary)',
		num: 'DA-002',
	},
];

export const mockWizardWorkPlans: WorkPlanOption[] = [
	{
		availableAmount: 400000,
		currency: 'USD',
		fiscalYear: 'FY2026',
		id: 1,
		name: 'Flood Infrastructure',
		plannedAmount: 2500000,
	},
	{
		availableAmount: 250000,
		currency: 'USD',
		fiscalYear: 'FY2026',
		id: 2,
		name: 'Technical Assistance',
		plannedAmount: 1000000,
	},
];

export const mockWizardActivities: ActivityOption[] = [
	{
		description: 'Initial phase of drainage system implementation',
		id: 1,
		name: 'Drainage System Construction - Phase 1',
		plannedAmount: 500000,
		responsibleEntityId: 1,
		responsibleEntityName: 'PIU - Metro Manila',
	},
	{
		description: 'Public education and engagement activities',
		id: 2,
		name: 'Community Awareness Program',
		plannedAmount: 75000,
		responsibleEntityId: 2,
		responsibleEntityName: 'Sub-PIU North',
	},
];

// Payment Portal Mock Data (PaymentPortalPage)

// Mock payment locations in Kenya
const mockPaymentLocations: PaymentLocation[] = [
	{
		city: 'Nairobi',
		country: 'Kenya',
		latitude: -1.2921,
		locationType: 'BANK',
		longitude: 36.8219,
		name: 'Kenya Commercial Bank - Nairobi',
	},
	{
		city: 'Mombasa',
		country: 'Kenya',
		latitude: -4.0547,
		locationType: 'MOBILE_OPERATOR',
		longitude: 39.6636,
		name: 'M-PESA Agent - Mombasa',
	},
	{
		city: 'Kisumu',
		country: 'Kenya',
		latitude: -0.0917,
		locationType: 'AGENT',
		longitude: 34.768,
		name: 'Cash Agent - Kisumu',
	},
	{
		city: 'Nakuru',
		country: 'Kenya',
		latitude: -0.3031,
		locationType: 'BANK',
		longitude: 36.08,
		name: 'Equity Bank - Nakuru',
	},
	{
		city: 'Eldoret',
		country: 'Kenya',
		latitude: 0.5143,
		locationType: 'BENEFICIARY_HOME',
		longitude: 35.2698,
		name: 'Home Delivery - Eldoret',
	},
	{
		city: 'Thika',
		country: 'Kenya',
		latitude: -1.0334,
		locationType: 'MOBILE_OPERATOR',
		longitude: 37.0693,
		name: 'Airtel Money - Thika',
	},
	{
		city: 'Nairobi',
		country: 'Kenya',
		latitude: -1.2636,
		locationType: 'AGENT',
		longitude: 36.8025,
		name: 'Hesapay Agent - Westlands',
	},
	{
		city: 'Machakos',
		country: 'Kenya',
		latitude: -1.5177,
		locationType: 'BANK',
		longitude: 37.2634,
		name: 'Cooperative Bank - Machakos',
	},
	{
		city: 'Garissa',
		country: 'Kenya',
		latitude: -0.4536,
		locationType: 'MOBILE_OPERATOR',
		longitude: 39.6401,
		name: 'M-PESA Agent - Garissa',
	},
	{
		city: 'Nyeri',
		country: 'Kenya',
		latitude: -0.4197,
		locationType: 'BANK',
		longitude: 36.9553,
		name: 'Family Bank - Nyeri',
	},
	{
		city: 'Meru',
		country: 'Kenya',
		latitude: 0.0476,
		locationType: 'AGENT',
		longitude: 37.6493,
		name: 'Cash Agent - Meru',
	},
	{
		city: 'Malindi',
		country: 'Kenya',
		latitude: -3.2192,
		locationType: 'MOBILE_OPERATOR',
		longitude: 40.1025,
		name: 'M-PESA Agent - Malindi',
	},
	{
		city: 'Naivasha',
		country: 'Kenya',
		latitude: -0.7172,
		locationType: 'BENEFICIARY_HOME',
		longitude: 36.4328,
		name: 'Home Delivery - Naivasha',
	},
	{
		city: 'Kitale',
		country: 'Kenya',
		latitude: 1.0157,
		locationType: 'BANK',
		longitude: 35.0062,
		name: 'Equity Bank - Kitale',
	},
	{
		city: 'Kakamega',
		country: 'Kenya',
		latitude: 0.2827,
		locationType: 'AGENT',
		longitude: 34.7519,
		name: 'Hesapay Agent - Kakamega',
	},
	{
		city: 'Bungoma',
		country: 'Kenya',
		latitude: 0.5635,
		locationType: 'MOBILE_OPERATOR',
		longitude: 34.5606,
		name: 'Airtel Money - Bungoma',
	},
	{
		city: 'Kericho',
		country: 'Kenya',
		latitude: -0.3689,
		locationType: 'BANK',
		longitude: 35.2863,
		name: 'KCB Bank - Kericho',
	},
	{
		city: 'Embu',
		country: 'Kenya',
		latitude: -0.5388,
		locationType: 'BENEFICIARY_HOME',
		longitude: 37.4596,
		name: 'Home Delivery - Embu',
	},
	{
		city: 'Kilifi',
		country: 'Kenya',
		latitude: -3.6305,
		locationType: 'MOBILE_OPERATOR',
		longitude: 39.8499,
		name: 'M-PESA Agent - Kilifi',
	},
	{
		city: 'Nairobi',
		country: 'Kenya',
		latitude: -1.2635,
		locationType: 'BANK',
		longitude: 36.8029,
		name: 'Standard Chartered - CBD',
	},
	{
		city: 'Nairobi',
		country: 'Kenya',
		latitude: -1.2188,
		locationType: 'AGENT',
		longitude: 36.8868,
		name: 'Cash Agent - Kasarani',
	},
	{
		city: 'Kisii',
		country: 'Kenya',
		latitude: -0.6817,
		locationType: 'MOBILE_OPERATOR',
		longitude: 34.766,
		name: 'M-PESA Agent - Kisii',
	},
	{
		city: 'Nanyuki',
		country: 'Kenya',
		latitude: 0.0167,
		locationType: 'BANK',
		longitude: 37.0667,
		name: 'Absa Bank - Nanyuki',
	},
	{
		city: 'Voi',
		country: 'Kenya',
		latitude: -3.396,
		locationType: 'AGENT',
		longitude: 38.556,
		name: 'Hesapay Agent - Voi',
	},
	{
		city: 'Isiolo',
		country: 'Kenya',
		latitude: 0.3556,
		locationType: 'BENEFICIARY_HOME',
		longitude: 37.5833,
		name: 'Home Delivery - Isiolo',
	},
];

// Mock extended vouchers for payment portal
const mockExtendedVouchers: ExtendedVoucher[] = [
	{
		amount: 15000,
		beneficiary: 'Jane Wanjiku',
		conditionMet: 'Attendance ≥95%',
		currency: 'USD',
		workPlan: 'School Feeding Program',
		id: 'ev-001',
		location: mockPaymentLocations[0],
		status: 'ISSUED',
		voucherId: 'VCH-2025-001',
	},
	{
		amount: 12500,
		beneficiary: 'Peter Ochieng',
		conditionMet: 'Invoice Verified',
		currency: 'USD',
		workPlan: 'Infrastructure Development',
		id: 'ev-002',
		location: mockPaymentLocations[1],
		status: 'ISSUED',
		voucherId: 'VCH-2025-002',
	},
	{
		amount: 8750,
		beneficiary: 'Mary Akinyi',
		conditionMet: 'Inspection Approved',
		currency: 'USD',
		workPlan: 'Technical Assistance',
		id: 'ev-003',
		location: mockPaymentLocations[2],
		status: 'PENDING',
		voucherId: 'VCH-2025-003',
	},
	{
		amount: 22000,
		beneficiary: 'John Kamau',
		conditionMet: 'PIU Authenticated',
		currency: 'USD',
		workPlan: 'Flood Infrastructure',
		id: 'ev-004',
		location: mockPaymentLocations[3],
		status: 'ISSUED',
		voucherId: 'VCH-2025-004',
	},
	{
		amount: 9500,
		beneficiary: 'Grace Njeri',
		conditionMet: 'Attendance ≥95%',
		currency: 'USD',
		workPlan: 'School Feeding Program',
		id: 'ev-005',
		location: mockPaymentLocations[4],
		status: 'PENDING',
		voucherId: 'VCH-2025-005',
	},
	{
		amount: 18000,
		beneficiary: 'Samuel Kipchoge',
		conditionMet: 'Invoice Verified',
		currency: 'USD',
		workPlan: 'Infrastructure Development',
		id: 'ev-006',
		location: mockPaymentLocations[5],
		status: 'ISSUED',
		voucherId: 'VCH-2025-006',
	},
	{
		amount: 7500,
		beneficiary: 'Faith Muthoni',
		conditionMet: 'Inspection Approved',
		currency: 'USD',
		workPlan: 'Technical Assistance',
		id: 'ev-007',
		location: mockPaymentLocations[6],
		status: 'ISSUED',
		voucherId: 'VCH-2025-007',
	},
	{
		amount: 31000,
		beneficiary: 'David Omondi',
		conditionMet: 'PIU Authenticated',
		currency: 'USD',
		workPlan: 'Flood Infrastructure',
		id: 'ev-008',
		location: mockPaymentLocations[7],
		status: 'ISSUED',
		voucherId: 'VCH-2025-008',
	},
	{
		amount: 14200,
		beneficiary: 'Sarah Wambui',
		conditionMet: 'Attendance ≥95%',
		currency: 'USD',
		workPlan: 'School Feeding Program',
		id: 'ev-009',
		location: mockPaymentLocations[0],
		status: 'PAID',
		voucherId: 'VCH-2025-009',
	},
	{
		amount: 26500,
		beneficiary: 'Michael Otieno',
		conditionMet: 'Invoice Verified',
		currency: 'USD',
		workPlan: 'Infrastructure Development',
		id: 'ev-010',
		location: mockPaymentLocations[1],
		status: 'REDEEMED',
		voucherId: 'VCH-2025-010',
	},
	{
		amount: 11200,
		beneficiary: 'Agnes Chebet',
		conditionMet: 'Attendance ≥95%',
		currency: 'USD',
		workPlan: 'School Feeding Program',
		id: 'ev-011',
		location: mockPaymentLocations[8],
		status: 'ISSUED',
		voucherId: 'VCH-2025-011',
	},
	{
		amount: 19800,
		beneficiary: 'Joseph Mutua',
		conditionMet: 'Invoice Verified',
		currency: 'USD',
		workPlan: 'Infrastructure Development',
		id: 'ev-012',
		location: mockPaymentLocations[9],
		status: 'PENDING',
		voucherId: 'VCH-2025-012',
	},
	{
		amount: 8900,
		beneficiary: 'Elizabeth Nyambura',
		conditionMet: 'PIU Authenticated',
		currency: 'USD',
		workPlan: 'Technical Assistance',
		id: 'ev-013',
		location: mockPaymentLocations[10],
		status: 'ISSUED',
		voucherId: 'VCH-2025-013',
	},
	{
		amount: 16500,
		beneficiary: 'Daniel Korir',
		conditionMet: 'Inspection Approved',
		currency: 'USD',
		workPlan: 'Flood Infrastructure',
		id: 'ev-014',
		location: mockPaymentLocations[11],
		status: 'ISSUED',
		voucherId: 'VCH-2025-014',
	},
	{
		amount: 13400,
		beneficiary: 'Rose Anyango',
		conditionMet: 'Attendance ≥95%',
		currency: 'USD',
		workPlan: 'School Feeding Program',
		id: 'ev-015',
		location: mockPaymentLocations[12],
		status: 'PENDING',
		voucherId: 'VCH-2025-015',
	},
	{
		amount: 27000,
		beneficiary: 'Stephen Kipruto',
		conditionMet: 'Invoice Verified',
		currency: 'USD',
		workPlan: 'Infrastructure Development',
		id: 'ev-016',
		location: mockPaymentLocations[13],
		status: 'ISSUED',
		voucherId: 'VCH-2025-016',
	},
	{
		amount: 9200,
		beneficiary: 'Patricia Wafula',
		conditionMet: 'PIU Authenticated',
		currency: 'USD',
		workPlan: 'Technical Assistance',
		id: 'ev-017',
		location: mockPaymentLocations[14],
		status: 'ISSUED',
		voucherId: 'VCH-2025-017',
	},
	{
		amount: 21500,
		beneficiary: 'Charles Wekesa',
		conditionMet: 'Inspection Approved',
		currency: 'USD',
		workPlan: 'Flood Infrastructure',
		id: 'ev-018',
		location: mockPaymentLocations[15],
		status: 'PENDING',
		voucherId: 'VCH-2025-018',
	},
	{
		amount: 14800,
		beneficiary: 'Lucy Cherono',
		conditionMet: 'Attendance ≥95%',
		currency: 'USD',
		workPlan: 'School Feeding Program',
		id: 'ev-019',
		location: mockPaymentLocations[16],
		status: 'ISSUED',
		voucherId: 'VCH-2025-019',
	},
	{
		amount: 10500,
		beneficiary: 'Francis Muriuki',
		conditionMet: 'Invoice Verified',
		currency: 'USD',
		workPlan: 'Technical Assistance',
		id: 'ev-020',
		location: mockPaymentLocations[17],
		status: 'ISSUED',
		voucherId: 'VCH-2025-020',
	},
	{
		amount: 23000,
		beneficiary: 'Catherine Jeptoo',
		conditionMet: 'PIU Authenticated',
		currency: 'USD',
		workPlan: 'Infrastructure Development',
		id: 'ev-021',
		location: mockPaymentLocations[18],
		status: 'PENDING',
		voucherId: 'VCH-2025-021',
	},
	{
		amount: 17600,
		beneficiary: 'James Kariuki',
		conditionMet: 'Inspection Approved',
		currency: 'USD',
		workPlan: 'Flood Infrastructure',
		id: 'ev-022',
		location: mockPaymentLocations[19],
		status: 'ISSUED',
		voucherId: 'VCH-2025-022',
	},
	{
		amount: 12300,
		beneficiary: 'Ann Moraa',
		conditionMet: 'Attendance ≥95%',
		currency: 'USD',
		workPlan: 'School Feeding Program',
		id: 'ev-023',
		location: mockPaymentLocations[20],
		status: 'ISSUED',
		voucherId: 'VCH-2025-023',
	},
	{
		amount: 28500,
		beneficiary: 'Robert Kiptoo',
		conditionMet: 'Invoice Verified',
		currency: 'USD',
		workPlan: 'Infrastructure Development',
		id: 'ev-024',
		location: mockPaymentLocations[21],
		status: 'PENDING',
		voucherId: 'VCH-2025-024',
	},
	{
		amount: 15700,
		beneficiary: 'Mercy Achieng',
		conditionMet: 'PIU Authenticated',
		currency: 'USD',
		workPlan: 'Technical Assistance',
		id: 'ev-025',
		location: mockPaymentLocations[22],
		status: 'ISSUED',
		voucherId: 'VCH-2025-025',
	},
	{
		amount: 20100,
		beneficiary: 'George Nganga',
		conditionMet: 'Inspection Approved',
		currency: 'USD',
		workPlan: 'Flood Infrastructure',
		id: 'ev-026',
		location: mockPaymentLocations[23],
		status: 'ISSUED',
		voucherId: 'VCH-2025-026',
	},
	{
		amount: 11800,
		beneficiary: 'Eunice Chepkoech',
		conditionMet: 'Attendance ≥95%',
		currency: 'USD',
		workPlan: 'School Feeding Program',
		id: 'ev-027',
		location: mockPaymentLocations[24],
		status: 'PENDING',
		voucherId: 'VCH-2025-027',
	},
];

/**
 * Get all extended vouchers for the payment portal
 */
export const getExtendedVouchers = (): ExtendedVoucher[] => {
	return mockExtendedVouchers;
};

/**
 * Get voucher statistics for the payment portal dashboard
 */
export const getVoucherStats = (): VoucherStats => {
	const vouchers = mockExtendedVouchers;

	const issuedVouchers = vouchers.filter((v) => v.status === 'ISSUED');
	const pendingVouchers = vouchers.filter((v) => v.status === 'PENDING');
	const paidVouchers = vouchers.filter((v) => v.status === 'PAID');
	const redeemedVouchers = vouchers.filter((v) => v.status === 'REDEEMED');

	return {
		issuedAmount: issuedVouchers.reduce((sum, v) => sum + v.amount, 0),
		issuedCount: issuedVouchers.length,
		paidAmount: paidVouchers.reduce((sum, v) => sum + v.amount, 0),
		paidCount: paidVouchers.length,
		pendingAmount: pendingVouchers.reduce((sum, v) => sum + v.amount, 0),
		pendingCount: pendingVouchers.length,
		redeemedAmount: redeemedVouchers.reduce((sum, v) => sum + v.amount, 0),
		redeemedCount: redeemedVouchers.length,
		totalAmount: vouchers.reduce((sum, v) => sum + v.amount, 0),
		totalCount: vouchers.length,
	};
};
